import { Injectable } from '@angular/core';

import { environment } from '../../environments/environment';
import {
  Partner,
  PartnerCollection,
  PartnerPostRequest,
  PartnerPutRequest,
  Account,
  AccountCollection,
  AccountPostRequest,
  AccountPutRequest,
  Group,
  GroupCollection,
  GroupPutRequest,
  Site,
  SitePostRequest,
  SitePutRequest,
  Client,
  ClientCollection,
  ClientPostRequest,
  ClientPutRequest,
  User,
  UserCollection,
  UserPostRequest,
  UserPutRequest,
  UserAccessCodePostRequest,
  UserAccessCodePutRequest,
  UserWelcomeEmailRequest,
  ContactCollection,
  Tag,
  SiteCollection,
  GroupPostRequest,
  MemberCollection
} from '@mitel/cloudlink-sdk/admin';
import { AdminService as CloudAdmin } from '@mitel/cloudlink-sdk/admin';
import { Odata } from '@mitel/cloudlink-sdk/utils';
import {AccountService} from './account.service';
import * as _ from 'lodash';
import { CloudLinkService } from './cloudlink.service';

@Injectable()
export class AdminService {
    private readonly api: CloudAdmin;

    constructor(
        private accountService: AccountService,
        cloudLinkService: CloudLinkService
    ) {
        this.api = cloudLinkService.adminService;
    }

    public getPartners(odata?: Odata, options?: any): Promise<PartnerCollection> {
        if (odata) {
            return this.api.getPartners({ top: odata.top, skip: odata.skip, skipToken: odata.skipToken, options }).then(response => response.body);
        } else {
            return this.api.getPartners({ options }).then(response => response.body);
        }
    }

    public getPartner(partnerId: string, options?: any): Promise<Partner> {
        return this.api.getPartner({ partnerId, options }).then(response => response.body);
    }

    public createPartner(body: PartnerPostRequest, options?: any): Promise<Partner> {
        return this.api.createPartner({body, options}).then(response => response.body);
    }

    public updatePartner(partnerId: string, body: PartnerPutRequest, options?: any): Promise<any> {
        return this.api.updatePartner({partnerId, body, options}).then(response => response.body);
    }

    public deletePartner(partnerId: string): Promise<any> {
        return this.api.deletePartner({partnerId}).then(response => response.body);
    }

    public getAccounts(odata?: Odata, options?: any): Promise<AccountCollection> {
        if (odata) {
            return this.api.getAccounts({
                expand: odata.expand, skip: odata.skip,
                top: odata.top, skipToken: odata.skipToken, options
            }).then(response => response.body);
        } else {
            return this.api.getAccounts({ options }).then(response => response.body);
        }
    }

    public getAccount(accountId: string): Promise<Account> {
        return this.api.getAccount({accountId}).then(response => response.body);
    }

    public createAccount(body: AccountPostRequest): Promise<Account> {
        return this.api.createAccount({ body }).then(response => response.body);
    }

    public updateAccount(accountId: string, body: AccountPutRequest): Promise<Account> {
        return this.api.updateAccount({accountId, body}).then(response => response.body);
    }

    public deleteAccount(accountId: string): Promise<any> {
        return this.api.deleteAccount({accountId}).then(response => response.body);
    }

    public async getFirstSite(accountId: string): Promise<Site> {
        return this.getSites(accountId).then(siteCollection => {
            if (siteCollection && siteCollection.count > 0 && siteCollection._embedded && siteCollection._embedded.items) {
                const sites = siteCollection._embedded.items;
                if (sites && sites[0]) {
                    return sites[0];
                }
            }
            return null;
        })
    }

    public getSites(accountId: string, odata?: Odata, options?: any): Promise<SiteCollection> {
        if (odata) {
            return this.api.getSites({ accountId, top: odata.top, skip: odata.skip,
                 expand: odata.expand, skipToken: odata.skipToken, options }).then(response => response.body);
        } else {
            return this.api.getSites({ accountId, options }).then(response => response.body);
        }
    }

    public getSite(accountId: string, siteId: string): Promise<Site> {
        return this.api.getSite({accountId, siteId}).then(response => response.body);
    }

    public createSite(accountId, body: SitePostRequest): Promise<Site> {
        return this.api.createSite({accountId, body}).then(response => response.body);
    }

    public updateSite(accountId: string, siteId: string, body: SitePutRequest): Promise<Site> {
        return this.api.updateSite({accountId, siteId, body}).then(response => response.body);
    }

    public deleteSite(accountId: string, siteId: string): Promise<any> {
        return this.api.deleteSite({accountId, siteId}).then(response => response.body);
    }

    public getClientsFromAccount(accountId: string, odata?: Odata, options?: any): Promise<ClientCollection> {
        if (odata) {
            return this.api.getClientsByAccountId({
                accountId, top: odata.top, skip: odata.skip,
                skipToken: odata.skipToken, filter: odata.filter, options
            }).then(response => response.body);
        } else {
            return this.api.getClientsByAccountId({ accountId, options }).then(response => response.body);
        }
    }

    public getClients(filter?: string, skip?: number, top?: number, skipToken?: string, options?: any): Promise<ClientCollection> {
        return this.api.getClients({ top, skip, filter, skipToken, options }).then(response => response.body);
    }

    public getClientFromAccount(accountId: string, clientId: string): Promise<Client> {
        return this.api.getClientByAccountId({ accountId, clientId }).then(response => response.body);
    }

    public getClient(clientId: string): Promise<Client> {
        return this.api.getClient({ clientId }).then(response => response.body);
    }

    public createClient(body: ClientPostRequest): Promise<Client> {
        return this.api.createClient({ body }).then(response => response.body);
    }

    public createClientByAccountId(accountId: string, body: ClientPostRequest): Promise<Client> {
        return this.api.createClientByAccountId({accountId, body}).then(response => response.body);
    }

    public deleteClientByAccountId(accountId: string, clientId: string): Promise<any> {
        return this.api.deleteClientByAccountId({accountId, clientId}).then(response => response.body);
    }


    public updateClient(clientId: string, body: ClientPutRequest): Promise<Client> {
        return this.api.updateClient({ clientId, body }).then(response => response.body);
    }

    public deleteClient(clientId: string): Promise<any> {
        return this.api.deleteClient({clientId}).then(response => response.body);
    }

    public createUser(body: UserPostRequest): Promise<User> {
        return this.api.createUser({body}).then(response => response.body);
    }

    public createUserForAccount(accountId: string, body: UserPostRequest): Promise<User> {
        return this.api.createUserByAccountId({accountId, body}).then(response => response.body);
    }

    public createAccessCode(userId: string, body: UserAccessCodePostRequest): Promise<any> {
        return this.api.sendUserAccessCode({ userId, body }).then(response => response.body);
    }

    public confirmAccessCode(userId: string, body: UserAccessCodePutRequest): Promise<User> {
        return this.api.verifyUserAccessCode({ userId, body }).then(response => response.body);
    }

    public getUsersFromAccount(accountId: string, odata?: Odata, options?: any): Promise<UserCollection> {
        if (odata) {
            return this.api.getUsersByAccountId({
                accountId, skip: odata.skip, top: odata.top, skipToken: odata.skipToken,
                filter: odata.filter, search: odata.search, expand: odata.expand, options
            }).then(response => response.body);
        } else {
            return this.api.getUsersByAccountId({ accountId, options }).then(response => response.body);
        }
    }

    public getUserFromAccount(accountId: string, userId: string): Promise<User> {
        return this.api.getUserByAccountId({accountId, userId}).then(response => response.body);
    }

    public getUsers(odata?: Odata, options?: any): Promise<UserCollection> {
        if (odata) {
            return this.api.getUsers({
                skip: odata.skip, top: odata.top, skipToken: odata.skipToken,
                filter: odata.filter, search: odata.search, expand: odata.expand, options
            }).then(response => response.body);
        } else {
            return this.api.getUsers({ options }).then(response => response.body);
        }
    }

    public getUser(userId: string): Promise<User> {
        return this.api.getUser({ userId }).then(response => response.body);
    }

    public getUserByAccountId(accountId: string, userId: string): Promise<User> {
        return this.api.getUserByAccountId({ accountId, userId }).then(response => response.body);
    }

    public getContactsByAccountId(accountId: string, odata?: Odata): Promise<ContactCollection> {
        if (odata) {
            return this.api.getAccountContacts({ accountId, filter: odata.filter, skip: odata.skip,
                top: odata.top, search: odata.search, skipToken: odata.skipToken }).then(response => response.body);
        } else {
            return this.api.getAccountContacts({ accountId }).then(response => response.body);
        }
    }

    public getMyUser(): Promise<User> {
        // Use 'me' keyword as the userId
        return this.getUser('me');
    }

    public updateUser(userId: string, body: UserPutRequest): Promise<User> {
        return this.api.updateUser({ userId, body }).then(response => response.body);
    }

    public updateUserFromAccount(accountId: string, userId: string, body: UserPutRequest): Promise<User> {
        return this.api.updateUserByAccountId({ accountId, userId, body }).then(response => response.body);
    }

    public deleteUser(userId: string): Promise<any> {
        return this.api.deleteUser({ userId }).then(response => response.body);
    }

    public deleteUserFromAccount(accountId: string, userId: string): Promise<any> {
        return this.api.deleteUserByAccountId({ accountId, userId }).then(response => response.body);
    }

    public uploadUserPhoto(userId: string, fileType: string, file: any): Promise<User> {
        return this.api.updateUserPhotoWithFileType({ userId: userId, body: file, fileType: fileType}).then(response => response.body);
    }

    public uploadUserPhotoFromAccount(accountId: string, userId: string, fileType: string, file: any): Promise<User> {
        return this.api.updateUserPhotoByAccountIdWithFileType({ accountId: accountId, userId: userId, body: file, fileType: fileType }).then(response => response.body);
    }

    public getUserTags(userId: string): Promise<any> {
        return this.api.getUserTags({ userId }).then(response => response.body);
    }

    public getUserTag(userId: string, tagId: string): Promise<any> {
        return this.api.getUserTag({ userId, tagId }).then(response => response.body);
    }

    public createUserTag(userId: string, body: any): Promise<any> {
        return this.api.createUserTag({ userId, body }).then(response => response.body);
    }

    public getUserTagsFromAccount(accountId: string, userId: string): Promise<any> {
        return this.api.getUserTagsByAccountId({accountId, userId}).then(response => response.body);
    }

    public getUserTagFromAccount(accountId: string, userId: string, tagId: string): Promise<any> {
        return this.api.getUserTagByAccountId({accountId, userId, tagId}).then(response => response.body);
    }

    public createUserTagFromAccount(accountId: string, userId: string, body: any): Promise<any> {
        return this.api.createUserTagByAccountId({accountId, userId, body}).then(response => response.body);
    }

    public getAccountTags(accountId: string): Promise<any> {
        return this.api.getAccountTags({accountId}).then(response => response.body);
    }

    public getAccountTag(accountId: string, tagId: string): Promise<any> {
        return this.api.getAccountTag({accountId, tagId}).then(response => response.body);
    }

    public createAccountTag(accountId: string, body: any): Promise<Tag> {
        return this.api.createAccountTag({accountId, body}).then(response => response.body);
    }

  /**
   * Updates existing tags and creates new ones if they don't exist.
   * Please use createAccountTags directly if know for certain all tags are non existent
   * @param accountId
   * @param body
   */
  public async tryUpdateAccountTags(accountId: string, body: any): Promise<any> {
        const existingTags = this.accountService.getAccountTags() || {};
        const updatedTags = {};
        let newTags;
        for (const updatedTagId of Object.keys(body)) {
          if (existingTags[updatedTagId] !== undefined) {
            if (!_.isEqual(existingTags[updatedTagId], body[updatedTagId])) {
              const tagValue = typeof(body[updatedTagId]) === 'string' ? JSON.stringify(body[updatedTagId]) : body[updatedTagId];
              const response = await this.updateAccountTag(accountId, updatedTagId, tagValue);
              delete response._links; // removes the _links property
              updatedTags[updatedTagId] = response;
            }
          } else {
              if (!newTags) {
                newTags = {};
              }
              newTags[updatedTagId] = body[updatedTagId];
            }
        }
        if (newTags) {
          return await this.createAccountTag(accountId, newTags);
        }
        return {...existingTags, ...updatedTags};
    }

    public updateAccountTag(accountId: string, tagId: string, body: any): Promise<Tag> {
        return this.api.updateAccountTag({accountId, tagId, body}).then(response => response.body);
    }

    public deleteAccountTag(accountId: string, tagId): Promise<any> {
        return this.api.deleteAccountTag({accountId, tagId}).then(response => response.body);
    }

    public getGroups(odata?: Odata, options?: any): Promise<GroupCollection> {
        if (odata) {
            return this.api.getGroups({ expand: odata.expand, filter: odata.filter, skip: odata.skip,
                top: odata.top, search: odata.search, options }).then(response => response.body);
        } else {
            return this.api.getGroups({ options }).then(response => response.body);
        }
    }

    public getGroupsFromAccount(accountId: string, odata?: Odata, options?: any): Promise<GroupCollection> {
        if (odata) {
            return this.api.getGroupsByAccountId({
                accountId, skip: odata.skip, top: odata.top,
                skipToken: odata.skipToken, filter: odata.filter,
                search: odata.search, expand: odata.expand, options
            }).then(response => response.body);
        } else {
            return this.api.getGroupsByAccountId({ accountId, options }).then(response => response.body);
        }
    }

    public getPhantomGroups(odata?: Odata, options?: any): Promise<GroupCollection> {
        if (odata) {
            return this.api.getGroups({
                filter: `type eq 'PARK' and subType eq 'PHANTOM'`, expand: odata.expand,
                skip: odata.skip, top: odata.top, options
            }).then(response => response.body);
        } else {
            return this.api.getGroups({ filter: `type eq 'PARK' and subType eq 'PHANTOM'`, options }).then(response => response.body);
        }
    }

    public createGroupByAccount(accountId: string, body: GroupPostRequest, options?: any): Promise<Group> {
        return this.api.createGroupByAccountId({ accountId, body, options }).then(response => response.body);
    }

    public getGroupFromAccount(accountId: string, groupId: string, options?: any): Promise<Group> {
        return this.api.getGroupByAccountId({ accountId, groupId, options }).then(response => response.body);
    }

    public deleteGroupFromAccount(accountId: string, groupId: string, options?: any): Promise<Group> {
        return this.api.deleteGroupByAccountId({ accountId, groupId, options }).then(response => response.body);
    }

    public updateGroupFromAccount(accountId: string, groupId: string, body: GroupPutRequest): Promise<Group> {
        return this.api.updateGroupByAccountId({ accountId, groupId, body }).then(response => response.body);
    }

    public updateGroup(groupId: string, body: GroupPutRequest): Promise<Group> {
        return this.api.updateGroup({groupId, body}).then(response => response.body);
    }

    public getGroupsWithTagId(tagId: string, odata?: Odata, options?: any): Promise<GroupCollection> {
        if (odata) {
            return this.api.getGroupsWithTag({
                tagId, skip: odata.skip, top: odata.top, skipToken: odata.skipToken,
                filter: odata.filter, search: odata.filter, options
            }).then(response => response.body);
        } else {
            return this.api.getGroupsWithTag({ tagId, options }).then(response => response.body);
        }
    }

    public getGroupsWithTagIdFromAccount(accountId: string, tagId: string, odata?: Odata, options?: any): Promise<GroupCollection> {
        if (odata) {
            // TODO: remove when odata for getGroupsWithTagIdFromAccount is supported in cloudlink-sdk/admin
            options = options || {};
            const queryParameter = {} as any;

            if (odata.skip !== undefined) {
                queryParameter['skip'] = odata.skip;
            }
            if (odata.top !== undefined) {
                queryParameter['top'] = odata.top;
            }
            if (odata.filter !== undefined) {
                queryParameter['filter'] = odata.filter;
            }
            if (odata.search !== undefined) {
                queryParameter['search'] = odata.search;
            }
            if (odata.skipToken !== undefined) {
                queryParameter['skipToken'] = odata.skipToken;
            }

            options.query = Object.assign({}, queryParameter, options.query);
            return this.api.getGroupsWithTagByAccountId({ accountId, tagId, options }).then(response => response.body);
        } else {
            return this.api.getGroupsWithTagByAccountId({ accountId, tagId, options }).then(response => response.body);
        }
    }

    public createGroupTag(groupId: string, body: any): Promise<Tag> {
        return this.api.createGroupTag({groupId, body}).then(response => response.body);
    }

    public createGroupTagFromAccount(accountId: string, groupId: string, body: any): Promise<Tag> {
        return this.api.createGroupTagByAccountId({accountId, groupId, body}).then(response => response.body);
    }

    public updateGroupTag(groupId: string, tagId: string, body: any): Promise<Tag> {
        return this.api.updateGroupTag({groupId, tagId, body}).then(response => response.body);
    }

    public updateGroupTagFromAccount(accountId: string, groupId: string, tagId: string, body: any): Promise<Tag> {
        return this.api.updateGroupTagByAccountId({accountId, groupId, tagId, body}).then(response => response.body);
    }

    public deleteGroupTag(groupId: string, tagId: string): Promise<any> {
        return this.api.deleteGroupTag({groupId, tagId}).then(response => response.body);
    }

    public deleteGroupTagFromAccount(accountId: string, groupId: string, tagId: string): Promise<any> {
        return this.api.deleteGroupTagByAccountId({accountId, groupId, tagId}).then(response => response.body);
    }

    public generateRandomPassword(length: number): string {
        const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ1234567890';
        let pass = '';
        for (let x = 0; x < length; x++) {
            const i = Math.floor(Math.random() * chars.length);
            pass += chars.charAt(i);
        }
        return pass;
    }

  public sendWelcomeEmailToUser(accountId: string, userId: string, body: UserWelcomeEmailRequest): Promise<void> {
    return this.api.sendWelcomeToUserByAccountId({accountId, userId, body}).then(response => response.body);
  }

  public sendWelcomeEmail(accountId: string, email: string, body: UserWelcomeEmailRequest): Promise<void> {
    return this.api.sendWelcomeByAccountId({accountId, email, body}).then(response => response.body);
  }

  public getGroupMembers(accountId: string, groupId: string, options?: any): Promise<MemberCollection> {
    return this.api.getGroupMembersByAccountId({accountId, groupId, options}).then(response => response.body);
  }

}
